import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';

import { Button, ButtonSize, Rounding } from '../../../shared/components/button/button';
import { Gap } from '../../../shared/components/gap/gap';
import { ProgressBar } from '../../../shared/components/progress-bar/progress-bar';
import { useDesktop } from '../../../shared/hooks/breakpoint';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { slugify } from '../../../shared/utils/strings';
import { IConsumeTypeStrategy } from '../../graphql/available-bonuses.graphql';

import styles from './active-bonus-card.module.scss';
const style = createStyleHelper(styles, 'active-bonus-card');

export interface IActiveBonusCardProps {
    className?: string;
    id: string;
    amount: number;
    currency: string;
    title: string;
    wagered: number;
    consumeTypeStrategy?: IConsumeTypeStrategy | null;
    expiryDate?: string | null;
    onForfeitBonus: (id: string) => void;
    isForfeiting: boolean;
}

export const ActiveBonusCard: FunctionComponent<IActiveBonusCardProps> = ({
    className,
    id,
    amount,
    currency,
    title,
    wagered,
    expiryDate,
    onForfeitBonus,
    consumeTypeStrategy,
    isForfeiting,
}) => {
    const isFullDisplay = useDesktop();

    const forfeitBonus = useCallback(() => {
        onForfeitBonus(id);
    }, [onForfeitBonus, id]);

    return (
        <div className={createClassName([className, style()])}>
            <Gap className={style('content')}>
                <div className={style('label')}>
                    <FormattedMessage
                        id="account.bonuses.description"
                        defaultMessage="Description"
                    />
                </div>
                <h2 className={style('title')}>{title}</h2>
                {consumeTypeStrategy ? (
                    <p className={style('consume-type')}>
                        <FormattedMessage
                            id={`account.bonuses.consume-type.${slugify(consumeTypeStrategy)}`}
                            defaultMessage={
                                consumeTypeStrategy === IConsumeTypeStrategy.BonusMoneyFirst
                                    ? 'Bonus money will be consumed first'
                                    : 'Your real money will be consumed first'
                            }
                        />
                    </p>
                ) : null}
            </Gap>
            <Gap className={style('amount')}>
                <div className={style('label')}>
                    <FormattedMessage id="account.bonuses.amount" defaultMessage="Amount" />
                </div>
                <FormattedNumber value={amount} currency={currency} style={'currency'} />
            </Gap>
            <Gap className={style('expiry')}>
                <div className={style('label')}>
                    <FormattedMessage id="account.bonuses.expires" defaultMessage="Expires" />
                </div>
                {expiryDate ? (
                    <time>
                        <FormattedDate value={expiryDate} />
                    </time>
                ) : (
                    <FormattedMessage
                        id="account.bonuses.expires.no-expiration"
                        defaultMessage={'No Expiration'}
                    />
                )}
            </Gap>
            <Gap className={style('wagered')}>
                <div className={style('wagered-info')}>
                    <div className={style('label', ['wagered'])}>
                        <FormattedMessage id="account.bonuses.wagered" defaultMessage="Wagered" />
                    </div>
                    <div className={style('wagered-percentage')}>
                        {`${Math.round(wagered ? wagered : 0)}%`}
                    </div>
                </div>
                <ProgressBar
                    percentageValue={wagered}
                    light={isFullDisplay}
                    value={isFullDisplay ? `${Math.round(wagered ? wagered : 0)}%` : undefined}
                />
            </Gap>
            <div className={style('actions')}>
                <Button
                    className={style('button')}
                    size={ButtonSize.MEDIUM}
                    rounding={Rounding.SMALL}
                    warning
                    disabled={isForfeiting}
                    onClick={forfeitBonus}
                >
                    <FormattedMessage
                        id="account.bonuses.forfeit-bonus"
                        defaultMessage="Forfeit Bonus"
                    />
                </Button>
            </div>
        </div>
    );
};
